import { PencilLine, X } from "lucide-react";

import { Button } from "@/components/button";

interface ResumeSessionPromptProps {
  isBusy: boolean;
  onClose: () => void;
  onContinue: () => void;
  onStartFresh: () => void;
}

/**
 * Inline prompt rendered in place of the upload step when the backend reports
 * an `active` uploading session for this designer + request. The designer
 * picks between resuming the existing session (rehydrate snapshot) or
 * discarding it and starting fresh.
 */
export function ResumeSessionPrompt({
  isBusy,
  onClose,
  onContinue,
  onStartFresh,
}: ResumeSessionPromptProps) {
  return (
    <div className="flex flex-col">
      <div className="flex items-start justify-end px-6 pt-4">
        <button
          aria-label="Close"
          className="rounded-md p-1.5 text-text-tertiary transition-colors hover:bg-bg-secondary"
          onClick={onClose}
          type="button"
        >
          <X size={20} />
        </button>
      </div>

      <div className="flex flex-col gap-6 px-6 pb-6">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-bg-brand-secondary">
          <PencilLine className="text-text-brand-primary" size={22} />
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-xl font-semibold text-text-primary">
            You have an upload in progress
          </span>
          <p className="text-sm text-text-tertiary">
            We found files from your last session still pending review. You can
            pick up where you left off or start fresh with new files.
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <Button
            color="color"
            isDisabled={isBusy}
            onPress={onContinue}
            size="lg"
            variant="primary"
          >
            {isBusy ? "Loading..." : "Continue where I left off"}
          </Button>
          <Button
            color="gray"
            isDisabled={isBusy}
            onPress={onStartFresh}
            size="lg"
            variant="secondary"
          >
            Start over with new files
          </Button>
        </div>
      </div>
    </div>
  );
}
